(() => {

  // WALPEX Collections → Cinematic transition (isolated + safe fallback).
  const loadWpGsap = () => new Promise(resolve => {
    if (window.gsap && window.ScrollTrigger) {
      try { window.gsap.registerPlugin(window.ScrollTrigger); } catch {}
      resolve(true);
      return;
    }
    if (window.__walpexGsapPromise) {
      window.__walpexGsapPromise.then(resolve).catch(() => resolve(false));
      return;
    }

    window.__walpexGsapPromise = new Promise(done => {
      const core = document.createElement('script');
      core.src = 'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/gsap.min.js';
      core.async = true;
      core.onload = () => {
        const plugin = document.createElement('script');
        plugin.src = 'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/ScrollTrigger.min.js';
        plugin.async = true;
        plugin.onload = () => {
          try {
            if (!window.gsap || !window.ScrollTrigger) return done(false);
            window.gsap.registerPlugin(window.ScrollTrigger);
            done(true);
          } catch {
            done(false);
          }
        };
        plugin.onerror = () => done(false);
        document.head.appendChild(plugin);
      };
      core.onerror = () => done(false);
      document.head.appendChild(core);
    });

    window.__walpexGsapPromise.then(resolve).catch(() => resolve(false));
  });

  const wpCollectionsCinematicTransition = async () => {
    if (document.documentElement.dataset.wpCinematicTransitionInit === 'true') return;
    document.documentElement.dataset.wpCinematicTransitionInit = 'true';

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const collections = document.querySelector('.wp-collections-section');
    const showcase = document.querySelector('.wp-showcase');
    if (!collections || !showcase) return;

    const collectionsVideo = collections.querySelector(
      '.wp-collections-section__background-video video'
    );
    const showcaseMedia = showcase.querySelector('.wp-showcase__media');
    const showcaseInner = showcase.querySelector('.wp-showcase__inner');
    const collectionCards = collections.querySelectorAll('.wp-collection-card');
    const collectionHeader = collections.querySelector('.wp-collections-section__header');

    if (!collectionsVideo || !showcaseMedia) return;

    // Same principle as Cinematic → Product Reel: use the actual section
    // overlap as the crossfade range. No gradient or second animation system.
    const updateCrossfade = () => {
      const rect = showcase.getBoundingClientRect();
      const range = Math.max(1, window.innerHeight * 0.42);
      const progress = Math.max(0, Math.min(1, (window.innerHeight - rect.top) / range));

      showcase.style.setProperty('--collections-crossfade', progress.toFixed(3));

      const cinematicOpacity = progress;
      showcaseMedia.style.opacity = String(cinematicOpacity);
      if (showcaseInner) showcaseInner.style.opacity = String(Math.max(0, Math.min(1, progress / 0.72)));

      collectionsVideo.style.opacity = String(1 - progress);
      collectionCards.forEach((card, index) => {
        const start = 0.82 + Math.min(0.10, index * 0.025);
        const p = Math.max(0, Math.min(1, (progress - start) / (1 - start)));
        card.style.opacity = String(1 - p);
        card.style.transform = `translate3d(0, ${(-7 * p).toFixed(2)}px, 0)`;
      });

      if (collectionHeader) {
        const p = Math.max(0, Math.min(1, (progress - 0.20) / 0.55));
        collectionHeader.style.opacity = String(1 - p);
        collectionHeader.style.transform = `translate3d(0, ${(-8 * p).toFixed(2)}px, 0)`;
      }
    };

    let raf = 0;
    const requestCrossfade = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        updateCrossfade();
      });
    };

    window.addEventListener('scroll', requestCrossfade, {passive:true});
    window.addEventListener('resize', requestCrossfade);
    requestCrossfade();
  };

  // Expose only this initializer so nested Theme Editor modules can call it safely.
  window.wpCollectionsCinematicTransition = wpCollectionsCinematicTransition;

  // WALPEX navigation/menu
  const initMenu = () => {
    // Mobile menu uses delegated events so it also works after Shopify
    // Theme Editor section reloads.
    if (document.documentElement.dataset.wpMenuDelegated === 'true') return;
    document.documentElement.dataset.wpMenuDelegated = 'true';

    document.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-wp-menu-toggle]');
      if (toggle) {
        event.preventDefault();
        event.stopPropagation();

        const menu = document.getElementById(toggle.getAttribute('aria-controls') || 'WPMobileMenu');
        if (!menu) return;

        const isOpen = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!isOpen));
        menu.hidden = isOpen;
        menu.classList.toggle('is-open', !isOpen);
        document.body.classList.toggle('wp-menu-open', !isOpen);

        // Force the open state in the Theme Editor/mobile preview.
        if (!isOpen) {
          menu.style.display = 'block';
          menu.style.visibility = 'visible';
          menu.style.opacity = '1';
        } else {
          menu.style.display = '';
          menu.style.visibility = '';
          menu.style.opacity = '';
        }
        return;
      }

      const close = event.target.closest('[data-wp-menu-close]');
      if (close) {
        const menu = close.closest('[data-wp-mobile-menu]');
        const toggle = document.querySelector('[data-wp-menu-toggle]');
        if (menu) {
          menu.hidden = true;
          menu.classList.remove('is-open');
          menu.style.display = '';
          menu.style.visibility = '';
          menu.style.opacity = '';
        }
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('wp-menu-open');
        return;
      }

      const link = event.target.closest('[data-wp-mobile-menu] a');
      if (link) {
        const menu = link.closest('[data-wp-mobile-menu]');
        const toggle = document.querySelector('[data-wp-menu-toggle]');
        if (menu) {
          menu.hidden = true;
          menu.classList.remove('is-open');
          menu.style.display = '';
          menu.style.visibility = '';
          menu.style.opacity = '';
        }
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('wp-menu-open');
      }
    }, true);
  };

  // Product state. This is deliberately delegated so Shopify Theme Editor section reloads
  // do not break the variant picker.
  const getProductRoot = (el) => el?.closest?.('[data-wp-product-page]') || null;
  const getProduct = root => {
    if (!root) return null;
    if (root.__walpexProduct) return root.__walpexProduct;
    const node = root.querySelector('[data-product-json]');
    if (!node) return null;
    try { root.__walpexProduct = JSON.parse(node.textContent); return root.__walpexProduct; }
    catch (e) { console.error('WALPEX product JSON error', e); return null; }
  };
  const formatMoney = (root, cents) => {
    const node = root?.querySelector('[data-money-format]');
    const currency = window.WALPEX_CART?.currency || 'GBP';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {style:'currency',currency}).format((Number(cents)||0)/100);
    } catch { return `${currency} ${((Number(cents)||0)/100).toFixed(2)}`; }
  };

  const variantOptions = (product, variant) => {
    const count = Array.isArray(product?.options) ? product.options.length : 1;
    if (Array.isArray(variant?.options)) return variant.options.slice(0, count);
    return Array.from({length: count}, (_, i) => variant?.[`option${i + 1}`] ?? '');
  };

  const selectedValues = root => {
    const product = getProduct(root);
    if (!product) return [];
    const count = Array.isArray(product.options) ? product.options.length : 1;
    return Array.from({length: count}, (_, index) => {
      const b = root.querySelector(`[data-option-index=\"${index}\"].is-selected`);
      return b ? String(b.dataset.optionValue ?? '') : '';
    });
  };

  const exactVariant = root => {
    const product = getProduct(root);
    if (!product || !Array.isArray(product.variants) || !product.variants.length) return null;
    // Products with a single/default variant have no option buttons in the UI.
    // In that case there is nothing to match, so use Shopify's only variant directly.
    if (product.has_only_default_variant || product.variants.length === 1) return product.variants[0];
    const values = selectedValues(root);
    return product.variants.find(v => {
      const opts = variantOptions(product, v);
      return opts.length === values.length && opts.every((x, i) => String(x ?? '') === String(values[i] ?? ''));
    }) || null;
  };

  const getVariantMedia = (root, variant) => {
    const node = root?.querySelector('[data-variant-media-map]') || document.querySelector('[data-variant-media-map]');
    if (!node || !variant) return null;
    if (!root.__walpexVariantMedia) {
      try { root.__walpexVariantMedia = JSON.parse(node.textContent || '{}'); }
      catch (e) { root.__walpexVariantMedia = {}; console.error('WALPEX variant media map error', e); }
    }
    return root.__walpexVariantMedia[String(variant.id)] || null;
  };

  const activateGalleryTarget = (root, target) => {
    const gallery = root?.querySelector('[data-product-gallery]');
    if (!gallery || !target) return;

    const items = [...gallery.querySelectorAll('[data-product-image]')];
    const thumbs = [...root.querySelectorAll('[data-product-thumbs] [data-thumb]')];
    const index = items.indexOf(target);
    if (index < 0) return;

    items.forEach((item, i) => {
      item.classList.toggle('is-active', i === index);
      item.setAttribute('aria-hidden', i === index ? 'false' : 'true');
    });

    thumbs.forEach((thumb, i) => {
      const active = i === index;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
    });

    const position = root.querySelector('[data-gallery-position]');
    if (position) position.textContent = `${index + 1} / ${items.length}`;
    const galleryCounter = root.querySelector('[data-gallery-nav-position]');
    if (galleryCounter) galleryCounter.textContent = `${index + 1} / ${items.length}`;
    gallery.dataset.activeIndex = String(index);
  };

  const activateGalleryIndex = (root, index) => {
    const gallery = root?.querySelector('[data-product-gallery]');
    if (!gallery) return;
    const items = [...gallery.querySelectorAll('[data-product-image]')];
    if (!items.length) return;
    const current = Number(gallery.dataset.activeIndex || 0);
    const next = (index + items.length) % items.length;
    activateGalleryTarget(root, items[next]);
  };

  const selectGalleryImage = (root, variant) => {
    const gallery = root?.querySelector('[data-product-gallery]');
    if (!gallery || !variant) return;

    const mapNode = root.querySelector('[data-variant-media-map]') || document.querySelector('[data-variant-media-map]');
    let mapped = null;
    if (mapNode) {
      try {
        const map = JSON.parse(mapNode.textContent || '{}');
        mapped = map[String(variant.id)] || null;
      } catch {}
    }

    const mediaId = mapped?.image_id ?? variant.featured_media?.id ?? variant.featured_image?.id ?? null;
    const src = mapped?.src || variant.featured_image?.src || '';
    const items = [...gallery.querySelectorAll('[data-product-image]')];
    let target = null;

    if (mediaId != null) {
      target = items.find(item => String(item.dataset.mediaId) === String(mediaId));
    }

    if (!target && src) {
      const clean = String(src).split('?')[0];
      target = items.find(item => {
        const declared = String(item.dataset.imageSrc || '').split('?')[0];
        const actual = String(item.querySelector('img')?.src || '').split('?')[0];
        return declared === clean || actual === clean || declared.endsWith(clean) || clean.endsWith(declared);
      });
    }

    if (!target && mapped?.position) {
      target = items[Number(mapped.position) - 1];
    }

    if (target) activateGalleryTarget(root, target);
  };

  const initGallery = root => {
    const gallery = root?.querySelector('[data-product-gallery]');
    if (!gallery || gallery.dataset.wpGalleryInit) return;
    gallery.dataset.wpGalleryInit = 'true';
    gallery.dataset.activeIndex = '0';

    // Main media itself is a deliberate control: clicking any visible image
    // leaves it selected and active.
    // Use capture phase for gallery controls. Shopify Theme Editor installs its own
    // click handler to select sections; capture lets our controls work inside the editor.
    const handleGalleryClick = event => {
      const target = event.target.closest('[data-gallery-prev],[data-gallery-next],[data-thumb],[data-product-image]');
      if (!target || !root.contains(target)) return;

      event.preventDefault();
      event.stopPropagation();

      if (target.matches('[data-gallery-prev]')) {
        const current = Number(gallery.dataset.activeIndex || 0);
        activateGalleryIndex(root, current - 1);
        return;
      }

      if (target.matches('[data-gallery-next]')) {
        const current = Number(gallery.dataset.activeIndex || 0);
        activateGalleryIndex(root, current + 1);
        return;
      }

      if (target.matches('[data-thumb]')) {
        const items = [...gallery.querySelectorAll('[data-product-image]')];
        const index = Number(target.dataset.thumb);
        if (items[index]) activateGalleryTarget(root, items[index]);
        return;
      }

      if (target.matches('[data-product-image]')) {
        activateGalleryTarget(root, target);
      }
    };
    root.addEventListener('click', handleGalleryClick, true);

    const media = root.querySelector('.wp-product-page__media');

    let touchStartY = null;
    gallery.addEventListener('touchstart', event => {
      touchStartY = event.touches[0].clientY;
    }, {passive:true});

    gallery.addEventListener('touchend', event => {
      if (touchStartY === null) return;
      const delta = event.changedTouches[0].clientY - touchStartY;
      if (Math.abs(delta) > 45) {
        const current = Number(gallery.dataset.activeIndex || 0);
        activateGalleryIndex(root, current + (delta < 0 ? 1 : -1));
      }
      touchStartY = null;
    }, {passive:true});

    // Optional click-to-zoom for product media.
    if (root.dataset.wpZoom === 'true') {
      const zoom = root.querySelector('[data-gallery-zoom]');
      const zoomImage = root.querySelector('[data-gallery-zoom-image]');
      const zoomClose = root.querySelector('[data-gallery-zoom-close]');

      const openZoom = item => {
        const src = item?.dataset?.zoomSrc;
        const img = item?.querySelector('img');
        if (!zoom || !zoomImage || !src) return;
        zoomImage.src = src;
        zoomImage.alt = img?.alt || '';
        zoom.hidden = false;
        zoom.setAttribute('aria-hidden','false');
        document.documentElement.classList.add('wp-gallery-zoom-open');
      };

      const closeZoom = () => {
        if (!zoom) return;
        zoom.hidden = true;
        zoom.setAttribute('aria-hidden','true');
        document.documentElement.classList.remove('wp-gallery-zoom-open');
      };

      if (zoomClose) zoomClose.addEventListener('click', closeZoom);
      zoom?.addEventListener('click', e => {
        if (e.target === zoom) closeZoom();
      });
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape') closeZoom();
      });

      root.addEventListener('click', e => {
        const trigger = e.target.closest('[data-gallery-zoom-trigger]');
        if (trigger) {
          const items = [...root.querySelectorAll('[data-product-image]')];
          const current = Number(gallery.dataset.activeIndex || 0);
          if (items[current]) openZoom(items[current]);
          return;
        }
        const item = e.target.closest('[data-product-image]');
        if (!item || e.target.closest('[data-gallery-prev],[data-gallery-next],[data-thumb],[data-gallery-zoom-trigger]')) return;
        openZoom(item);
      }, true);
    }

  };

  const updateProductUI = root => {
    const product = getProduct(root);
    if (!product) return;
    const variant = exactVariant(root);
    if (!variant) return;

    const input = root.querySelector('[data-product-variant]');
    if (input) input.value = variant.id;

    const price = root.querySelector('[data-product-price]');
    if (price) price.textContent = formatMoney(root, variant.price);

    const mobilePrice = root.querySelector('[data-mobile-price]');
    if (mobilePrice) mobilePrice.textContent = formatMoney(root, variant.price);

    const available = variant.available !== false;
    const main = root.querySelector('[data-main-add-to-cart]');
    const checkout = root.querySelector('.wp-product-page__checkout');
    const mobile = root.querySelector('[data-mobile-add]');

    if (main) {
      main.disabled = !available;
      main.dataset.variantId = String(variant.id);
      const label = main.querySelector('span');
      if (label) label.textContent = available ? 'Add to cart' : 'Sold out';
    }
    if (checkout) {
      checkout.disabled = !available;
      checkout.dataset.variantId = String(variant.id);
    }
    if (mobile) {
      mobile.disabled = !available;
      mobile.dataset.variantId = String(variant.id);
      mobile.innerHTML = available ? 'Add to cart <b>+</b>' : 'Sold out';
    }

    root.querySelectorAll('[data-option-value]').forEach(b => {
      b.setAttribute('aria-pressed', b.classList.contains('is-selected') ? 'true' : 'false');
    });

    selectGalleryImage(root, variant);
  };

  const initProduct = root => {
    if (!root) return;
    const product = getProduct(root);
    if (!product || !Array.isArray(product.variants)) return;
    initGallery(root);

    const input = root.querySelector('[data-product-variant]');
    let initial = product.variants.find(v => String(v.id) === String(input?.value));
    if (!initial || initial.available === false) initial = product.variants.find(v => v.available !== false) || initial || product.variants[0];

    if (initial) {
      const opts = variantOptions(product, initial);
      opts.forEach((value, i) => {
        root.querySelectorAll(`[data-option-index=\"${i}\"]`).forEach(b => {
          b.classList.toggle('is-selected', String(b.dataset.optionValue ?? '') === String(value ?? ''));
        });
      });
      if (input) input.value = initial.id;
    }

    if (!root.dataset.wpProductInit) root.dataset.wpProductInit = 'true';
    updateProductUI(root);
  };

  const showStatus=(root,message,error=false)=>{
    let status=root.querySelector('[data-wp-product-status]');
    if(!status){status=document.createElement('div');status.className='wp-product-page__status';status.dataset.wpProductStatus='';root.querySelector('.wp-product-page__purchase')?.after(status);}
    status.textContent=message; status.dataset.error=error?'true':'false'; status.hidden=false;
    clearTimeout(status._timer); status._timer=setTimeout(()=>status.hidden=true,3500);
  };

  // Cart drawer
  const drawer=document.querySelector('[data-wp-cart-drawer]');
  const cartBody=drawer?.querySelector('[data-wp-cart-body]');
  const cartTotal=drawer?.querySelector('[data-wp-cart-total]');
  const cartCount=document.querySelectorAll('[data-wp-cart-count], .wp-cart-count');
  const cartMoney=cents=>{try{return new Intl.NumberFormat(document.documentElement.lang||undefined,{style:'currency',currency:window.WALPEX_CART?.currency||'GBP'}).format((cents||0)/100)}catch{return ((cents||0)/100).toFixed(2)}};
  const cartShowQty=()=>drawer?.dataset.showQuantity !== 'false';
  const cartShowSavings=()=>drawer?.dataset.showSavings !== 'false';
  const openCart=()=>{if(!drawer)return;drawer.setAttribute('aria-hidden','false');document.body.classList.add('wp-cart-open')};
  const closeCart=()=>{if(!drawer)return;drawer.setAttribute('aria-hidden','true');document.body.classList.remove('wp-cart-open')};
  const esc=v=>String(v??'').replace(/[&<>'"]/g,c=>({'&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;'}[c]));
  const renderCart=cart=>{
    cartCount.forEach(n=>n.textContent=cart.item_count); if(cartTotal)cartTotal.textContent=cartMoney(cart.total_price); if(!cartBody)return;
    const savingsEl=drawer?.querySelector('[data-wp-cart-savings]');
    const savingsValue=drawer?.querySelector('[data-wp-cart-savings-value]');
    const savings=(cart.items||[]).reduce((sum,item)=>sum+Math.max(0,(item.original_line_price||0)-(item.final_line_price||0)),0);
    if(savingsEl && savingsValue){ savingsValue.textContent=cartMoney(savings); savingsEl.hidden=!cartShowSavings() || savings<=0; }
    if(!cart.items.length){
      const title=esc(drawer?.dataset.emptyTitle||'Your cart is empty.');
      const text=esc(drawer?.dataset.emptyText||'Explore the WALPEX collection and choose what belongs in your setup.');
      cartBody.innerHTML='<div class="wp-cart-empty"><span>00 / CART</span><h3>'+title+'</h3><p>'+text+'</p><a href="'+esc(window.WALPEX_CART?.rootUrl||'/')+'">Continue shopping <b>↗</b></a></div>';return;
    }
    cartBody.innerHTML=cart.items.map((item,i)=>`<article class="wp-cart-item" data-cart-line="${i+1}"><a class="wp-cart-item__image" href="${esc(item.url)}">${item.image?`<img src="${esc(item.image)}&width=180" alt="${esc(item.product_title)}" loading="lazy">`:''}</a><div class="wp-cart-item__info"><div class="wp-cart-item__meta">${esc(item.product_type||'WALPEX')}</div><a class="wp-cart-item__title" href="${esc(item.url)}">${esc(item.product_title)}</a>${item.variant_title&&item.variant_title!=='Default Title'?`<div class="wp-cart-item__variant">${esc(item.variant_title)}</div>`:''}<div class="wp-cart-item__row">${cartShowQty()?`<div class="wp-cart-item__qty"><button type="button" data-cart-qty="minus">−</button><span>${item.quantity}</span><button type="button" data-cart-qty="plus">+</button></div>`:`<span class="wp-cart-item__qty-static">Qty ${item.quantity}</span>`}<strong>${cartMoney(item.final_line_price)}</strong></div><button type="button" class="wp-cart-item__remove" data-cart-remove>Remove</button></div></article>`).join('');
  };
  const refreshCart=async()=>{const r=await fetch('/cart.js',{headers:{Accept:'application/json'}});if(!r.ok)throw Error('Cart request failed');const c=await r.json();renderCart(c);return c};
  const changeCart=async(line,quantity)=>{const r=await fetch('/cart/change.js',{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({line,quantity})});if(!r.ok)throw Error('Cart update failed');renderCart(await r.json())};

  const addToCart=async(form,checkoutMode=false)=>{
    const root=getProductRoot(form);
    const main=form.querySelector('[data-main-add-to-cart]');
    const checkout=form.querySelector('.wp-product-page__checkout');
    const product=getProduct(root);
    const variant=exactVariant(root);
    const qtyInput=form.querySelector('input[name="quantity"]');
    const quantity=Math.max(1,parseInt(qtyInput?.value,10)||1);

    if (!root || !product || !variant || variant.available === false) {
      if (root) showStatus(root,'This variant is currently unavailable.',true);
      return;
    }

    const activeButton=checkoutMode ? checkout : main;
    if (!activeButton || activeButton.disabled) return;

    const original=activeButton.textContent.trim();
    activeButton.disabled=true;
    if (checkoutMode) activeButton.textContent='Processing…';
    else if (main?.querySelector('span')) main.querySelector('span').textContent='Adding…';

    const data=new FormData(form);
    data.set('id',String(variant.id));
    data.set('quantity',String(quantity));

    try {
      const r=await fetch('/cart/add.js',{method:'POST',headers:{Accept:'application/json'},body:data});
      const payload=await r.json().catch(()=>({}));
      if(!r.ok) throw Error(payload.description||'This variant is currently unavailable.');

      if(checkoutMode){
        // In the Theme Editor Shopify intentionally blocks checkout navigation.
        // Keep the editor open and give the merchant a clear confirmation.
        if(window.Shopify?.designMode){
          showStatus(root,'Added to cart. Checkout opens on the live storefront.');
          await refreshCart();
          openCart();
        } else {
          // The cart checkout is known to work on the live storefront.
          // After adding the selected variant, use the same Shopify checkout
          // endpoint directly so Buy now cannot be intercepted by another
          // theme handler. The cart session/cookies from fetch are preserved.
          window.location.assign('/checkout');
        }
        return;
      }

      await refreshCart();
      openCart();
    } catch(e) {
      console.error(e);
      showStatus(root,e.message||'Unable to add this variant to cart.',true);
    } finally {
      activeButton.disabled=false;
      if(checkoutMode) activeButton.textContent=original || 'Buy now';
      else if(main?.querySelector('span')) main.querySelector('span').textContent='Add to cart';
      updateProductUI(root);
    }
  };

  // Theme-editor-safe gallery fallback. The Shopify preview inspector can sit
  // above the visual section and make normal button click targets unreliable.
  // Route pointer coordinates at the document capture phase instead of relying
  // on the clicked element being the gallery control itself.
  const galleryRootAtPoint = (x, y) => {
    const roots = [...document.querySelectorAll('[data-wp-product-page]')];
    return roots.find(root => {
      const r = root.getBoundingClientRect();
      return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
    }) || null;
  };

  let galleryPointer = null;
  const galleryPointerDown = e => {
    if (!e.isPrimary) return;
    const root = galleryRootAtPoint(e.clientX, e.clientY);
    const media = root?.querySelector('[data-gallery-shell]');
    const gallery = root?.querySelector('[data-product-gallery]');
    if (!root || !media || !gallery) return;
    const r = media.getBoundingClientRect();
    if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) return;
    galleryPointer = {root, x:e.clientX, y:e.clientY, time:Date.now()};
  };

  const galleryPointerUp = e => {
    if (!galleryPointer || !e.isPrimary) return;
    const {root,x,y,time} = galleryPointer;
    galleryPointer = null;
    const gallery = root.querySelector('[data-product-gallery]');
    const media = root.querySelector('[data-gallery-shell]');
    if (!gallery || !media) return;

    const dx=e.clientX-x, dy=e.clientY-y;
    const travel=Math.hypot(dx,dy);
    const current=Number(gallery.dataset.activeIndex||0);

    // Vertical swipe: keep the mobile behavior independent from click targets.
    if (Math.abs(dy)>45 && Math.abs(dy)>Math.abs(dx)) {
      e.preventDefault();
      e.stopPropagation();
      activateGalleryIndex(root,current+(dy<0?1:-1));
      return;
    }

    // Short tap: route it by its physical location, so a transparent editor
    // inspector layer cannot make the actual thumbnail/button irrelevant.
    if (travel>18 || Date.now()-time>900) return;
    const nav=media.querySelector('[data-gallery-prev]')?.parentElement;
    if (nav) {
      const nr=nav.getBoundingClientRect();
      if (e.clientX>=nr.left&&e.clientX<=nr.right&&e.clientY>=nr.top&&e.clientY<=nr.bottom) {
        const mid=(nr.left+nr.right)/2;
        e.preventDefault(); e.stopPropagation();
        activateGalleryIndex(root,e.clientX>mid?current+1:current-1);
        return;
      }
    }
    const thumbs=media.querySelector('[data-product-thumbs]');
    if (thumbs) {
      const tr=thumbs.getBoundingClientRect();
      if (e.clientX>=tr.left&&e.clientX<=tr.right&&e.clientY>=tr.top&&e.clientY<=tr.bottom) {
        const items=[...gallery.querySelectorAll('[data-product-image]')];
        const thumbsList=[...thumbs.querySelectorAll('[data-thumb]')];
        const hit=thumbsList.findIndex(th=>{const r=th.getBoundingClientRect();return e.clientX>=r.left&&e.clientX<=r.right&&e.clientY>=r.top&&e.clientY<=r.bottom;});
        if(hit>=0&&items[hit]) {
          e.preventDefault(); e.stopPropagation();
          activateGalleryTarget(root,items[hit]);
        }
      }
    }
  };

  document.addEventListener('pointerdown',galleryPointerDown,true);
  document.addEventListener('pointerup',galleryPointerUp,true);

  const boot=()=>{initMenu();document.querySelectorAll('[data-wp-product-page]').forEach(initProduct);if(drawer)refreshCart().catch(()=>{});};
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',boot,{once:true});else boot();
  document.addEventListener('shopify:section:load',e=>{e.target.querySelectorAll?.('[data-wp-product-page]').forEach(initProduct);});

  document.addEventListener('click',e=>{
    const option=e.target.closest('[data-option-value]');
    if(option){const root=getProductRoot(option);if(root){e.preventDefault();const index=option.dataset.optionIndex;root.querySelectorAll(`[data-option-index="${index}"]`).forEach(b=>b.classList.remove('is-selected'));option.classList.add('is-selected');updateProductUI(root);}return;}
    const qty=e.target.closest('[data-qty-minus],[data-qty-plus]');
    if(qty){
      const root=getProductRoot(qty);
      const input=root?.querySelector('input[name=\"quantity\"]');
      if(root&&input){
        e.preventDefault();
        const current=Math.max(1,parseInt(input.value,10)||1);
        input.value=String(qty.matches('[data-qty-plus]')?current+1:Math.max(1,current-1));
        input.dispatchEvent(new Event('change',{bubbles:true}));
      }
      return;
    }
    const mobile=e.target.closest('[data-mobile-add]');
    if(mobile){const root=getProductRoot(mobile);const main=root?.querySelector('[data-main-add-to-cart]');if(root&&main){e.preventDefault();if(!main.disabled)main.click();}return;}
    if(e.target.closest('[data-wp-cart-close]')){e.preventDefault();closeCart();return;}
    const cartLink=e.target.closest('.wp-cart-action');
    if(cartLink){e.preventDefault();refreshCart().then(openCart).catch(()=>{});return;}
    const line=e.target.closest('[data-cart-line]');
    if(line){const n=Number(line.dataset.cartLine);if(e.target.closest('[data-cart-remove]')){changeCart(n,0).catch(()=>{});return;}const q=e.target.closest('[data-cart-qty]');if(q){const current=Number(line.querySelector('.wp-cart-item__qty span')?.textContent||1);changeCart(n,q.dataset.cartQty==='plus'?current+1:Math.max(0,current-1)).catch(()=>{});}}
  });

  document.addEventListener('submit',e=>{
    const form=e.target.closest('#wp-product-form'); if(!form)return;
    const root=getProductRoot(form); if(!root)return;
    const submitter=e.submitter;
    e.preventDefault();
    addToCart(form,submitter?.name==='checkout');
  },true);
  document.addEventListener('keydown',e=>{if(e.key==='Escape')closeCart();});
})();


/* WALPEX CTA feedback — visual only */
(function () {
  "use strict";
  document.addEventListener("click", function (event) {
    var btn = event.target.closest("[data-main-add-to-cart], .wp-product-page__add");
    if (!btn || btn.disabled) return;
    btn.classList.remove("wp-cta-pulse");
    void btn.offsetWidth;
    btn.classList.add("wp-cta-pulse");
    window.setTimeout(function () {
      btn.classList.remove("wp-cta-pulse");
    }, 320);
  }, true);
})();


/* WALPEX Fly-to-Cart — visual-only effect */
(function () {
  "use strict";

  function flyToCart(button) {
    var img = document.querySelector(".wp-product-gallery img.wp-product-gallery__image.is-active")
           || document.querySelector(".wp-product-gallery img")
           || document.querySelector(".wp-product-page img");
    var cart = document.querySelector("[data-wp-cart-open]")
           || document.querySelector(".wp-cart-action")
           || document.querySelector("[data-cart-toggle]");
    if (!img || !cart) return;

    var ir = img.getBoundingClientRect();
    var cr = cart.getBoundingClientRect();
    var clone = img.cloneNode(true);

    clone.className = "wp-fly-to-cart";
    clone.removeAttribute("srcset");
    clone.removeAttribute("sizes");
    Object.assign(clone.style, {
      position: "fixed",
      left: ir.left + "px",
      top: ir.top + "px",
      width: Math.min(ir.width, 110) + "px",
      height: Math.min(ir.height, 110) + "px",
      objectFit: "cover",
      pointerEvents: "none",
      zIndex: "2147483646",
      margin: "0",
      borderRadius: "8px",
      opacity: "0.95",
      transition: "left .55s cubic-bezier(.2,.7,.2,1), top .55s cubic-bezier(.2,.7,.2,1), width .55s cubic-bezier(.2,.7,.2,1), height .55s cubic-bezier(.2,.7,.2,1), opacity .55s ease, transform .55s cubic-bezier(.2,.7,.2,1)"
    });

    document.body.appendChild(clone);

    requestAnimationFrame(function () {
      clone.style.left = (cr.left + cr.width / 2 - 16) + "px";
      clone.style.top = (cr.top + cr.height / 2 - 16) + "px";
      clone.style.width = "32px";
      clone.style.height = "32px";
      clone.style.opacity = "0.2";
      clone.style.transform = "scale(.72)";
    });

    window.setTimeout(function () {
      clone.remove();
      cart.classList.remove("wp-cart-target-pulse");
      void cart.offsetWidth;
      cart.classList.add("wp-cart-target-pulse");
      window.setTimeout(function () {
        cart.classList.remove("wp-cart-target-pulse");
      }, 260);
    }, 570);
  }

  document.addEventListener("click", function (event) {
    var button = event.target.closest("[data-main-add-to-cart]");
    if (!button || button.disabled) return;
    window.setTimeout(function () { flyToCart(button); }, 40);
  }, true);
})();


/* WALPEX Mobile Sticky Add to Cart — visual/UX layer */
(function () {
  "use strict";
  var bar = document.querySelector("[data-wp-sticky-atc]");
  if (!bar) return;

  var mainButton = document.querySelector("[data-main-add-to-cart]");
  var stickyButton = bar.querySelector("[data-wp-sticky-add-to-cart]");
  var price = bar.querySelector("[data-wp-sticky-price]");
  if (!mainButton || !stickyButton) return;

  function updateVisibility() {
    if (window.innerWidth > 749) {
      bar.classList.remove("is-visible");
      bar.setAttribute("aria-hidden", "true");
      return;
    }
    var rect = mainButton.getBoundingClientRect();
    var visible = rect.bottom <= 0 || rect.top >= window.innerHeight;
    bar.classList.toggle("is-visible", visible);
    bar.setAttribute("aria-hidden", visible ? "false" : "true");
  }

  function updatePrice() {
    var selected = document.querySelector("[data-main-variant-id]:checked, [data-main-variant-id].is-selected");
    var variantPrice = selected && selected.getAttribute("data-variant-price");
    if (price && variantPrice) price.textContent = variantPrice;
  }

  stickyButton.addEventListener("click", function () {
    mainButton.click();
  });

  window.addEventListener("scroll", updateVisibility, { passive: true });
  window.addEventListener("resize", updateVisibility, { passive: true });
  document.addEventListener("change", updatePrice);
  updatePrice();
  updateVisibility();
})();


/* WALPEX Smart Filters v1.8 */
(function () {
  "use strict";
  var root = document.querySelector("[data-wp-filters]");
  if (!root) return;

  var toggle = root.querySelector("[data-wp-filter-toggle]");
  var panel = root.querySelector("[data-wp-filter-panel]");

  if (toggle && panel) {
    toggle.addEventListener("click", function () {
      var open = root.classList.toggle("is-open");
      toggle.setAttribute("aria-expanded", open ? "true" : "false");
    });
  }

  root.querySelectorAll(".wp-catalog-filter__heading").forEach(function (button) {
    button.addEventListener("click", function () {
      var group = button.closest(".wp-catalog-filter");
      if (!group) return;
      var open = group.classList.toggle("is-open");
      button.setAttribute("aria-expanded", open ? "true" : "false");
    });
  });
})();


/* WALPEX Predictive Search v1.9 */
(function () {
  "use strict";

  var modal = document.querySelector("[data-wp-search-modal]");
  if (!modal) return;

  var input = modal.querySelector("[data-wp-search-input]");
  var results = modal.querySelector("[data-wp-search-results]");
  var form = modal.querySelector("[data-wp-search-form]");
  var viewAll = modal.querySelector("[data-wp-search-view-all]");
  var timer = null;
  var controller = null;

  function openSearch() {
    modal.classList.add("is-open");
    modal.setAttribute("aria-hidden", "false");
    document.documentElement.style.overflow = "hidden";
    setTimeout(function () { if (input) input.focus(); }, 80);
  }

  function closeSearch() {
    modal.classList.remove("is-open");
    modal.setAttribute("aria-hidden", "true");
    document.documentElement.style.overflow = "";
  }

  document.addEventListener("click", function (e) {
    var inlineTrigger = e.target.closest(".wp-search-action--inline-trigger");
    if (inlineTrigger) {
      e.preventDefault();
      if (window.matchMedia && window.matchMedia("(max-width: 749px)").matches) {
        openSearch();
      } else {
        var inlineRoot = document.querySelector("[data-wp-inline-search]");
        var inlineInput = inlineRoot && inlineRoot.querySelector("[data-wp-inline-input]");
        if (inlineInput) { inlineInput.focus(); inlineRoot.classList.add("is-active"); }
      }
    }
    var opener = e.target.closest("[data-wp-search-open]");
    if (opener) {
      e.preventDefault();
      openSearch();
    }
    if (e.target.closest("[data-wp-search-close]")) {
      e.preventDefault();
      closeSearch();
    }
  });

  function openCartDrawer() {
    var selectors = [
      "[data-cart-drawer-toggle]",
      "[data-cart-toggle]",
      "[data-open-cart]",
      "[aria-controls*='cart']",
      "a[href*='/cart']"
    ];

    for (var i = 0; i < selectors.length; i++) {
      var el = document.querySelector(selectors[i]);
      if (el && !el.closest(".wp-quick-view")) {
        el.click();
        return true;
      }
    }

    // Theme-independent fallback: dispatch common cart events and return
    // false if the theme does not expose a drawer trigger.
    document.dispatchEvent(new CustomEvent("cart:open"));
    document.dispatchEvent(new CustomEvent("cart-drawer:open"));
    document.dispatchEvent(new CustomEvent("open-cart-drawer"));
    return false;
  }

  document.addEventListener("click", function (e) {
    var cartLink = e.target.closest("[data-wp-open-cart]");
    if (!cartLink) return;
    e.preventDefault();
    openCartDrawer();
  });

  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && modal.classList.contains("is-open")) closeSearch();
    if (e.key === "/" && !e.ctrlKey && !e.metaKey && !e.altKey &&
        document.activeElement !== input &&
        !/INPUT|TEXTAREA|SELECT/.test(document.activeElement.tagName)) {
      e.preventDefault();
      openSearch();
    }
  });

  var clear = modal.querySelector("[data-wp-search-clear]");
  if (clear) {
    clear.addEventListener("click", function () {
      input.value = "";
      input.focus();
      results.innerHTML = '<div class="wp-search-results__hint">Start typing to search products.</div>';
      viewAll.href = "/search";
    });
  }

  function render(data, term) {
    var products = (data && data.resources && data.resources.results &&
      data.resources.results.products) || [];

    if (!products.length) {
      results.innerHTML = '<div class="wp-search-results__hint">No products found.</div>';
      return;
    }

    var html = '<p class="wp-search-results__title">Products</p><div class="wp-search-results__grid">';
    products.slice(0, 8).forEach(function (p) {
      var img = p.image || (p.featured_image && p.featured_image.url) || "";
      var price = p.price || (p.price_min ? p.price_min : "");
      html += '<a class="wp-search-result" href="' + p.url + '">'
        + '<div class="wp-search-result__media">'
        + (img ? '<img src="' + img + '" alt="' + (p.title || "").replace(/"/g, "&quot;") + '" loading="lazy">' : "")
        + '</div>'
        + '<div class="wp-search-result__info">'
        + '<span class="wp-search-result__title">' + p.title + '</span>'
        + '<span class="wp-search-result__price">' + price + '</span>'
        + '</div></a>';
    });
    html += "</div>";
    results.innerHTML = html;
    viewAll.href = "/search?q=" + encodeURIComponent(term);
  }

  function search(term) {
    if (controller) controller.abort();
    if (!term.trim()) {
      results.innerHTML = '<div class="wp-search-results__hint">Start typing to search products.</div>';
      viewAll.href = "/search";
      return;
    }

    controller = new AbortController();
    results.innerHTML = '<div class="wp-search-results__hint">Searching...</div>';

    fetch("/search/suggest.json?q=" + encodeURIComponent(term) +
      "&resources[type]=product&resources[limit]=" + (document.querySelector("[data-wp-search-limit]")?.getAttribute("data-wp-search-limit") || "8") + "&resources[options][unavailable_products]=hide", {
        signal: controller.signal,
        headers: { "Accept": "application/json" }
      })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) { if (data) render(data, term); })
      .catch(function (err) {
        if (err && err.name === "AbortError") return;
        results.innerHTML = '<div class="wp-search-results__hint">Search is temporarily unavailable.</div>';
      });
  }

  if (input) {
    input.addEventListener("input", function () {
      clearTimeout(timer);
      var term = input.value;
      timer = setTimeout(function () { search(term); }, 180);
    });
  }

  if (form) {
    form.addEventListener("submit", function () {
      // Normal Shopify /search fallback remains available on Enter.
    });
  }
})();


/* WALPEX Inline Desktop Predictive Search — v1.9b */
(function () {
  "use strict";

  var root = document.querySelector("[data-wp-inline-search]");
  if (!root) return;

  var input = root.querySelector("[data-wp-inline-input]");
  var results = root.querySelector("[data-wp-inline-results]");
  var clear = root.querySelector("[data-wp-inline-clear]");
  var timer = null;
  var controller = null;

  function esc(value) {
    return String(value || "").replace(/[&<>"']/g, function (m) {
      return ({ "&":"&amp;", "<":"&lt;", ">":"&gt;", '"':"&quot;", "'":"&#39;" })[m];
    });
  }

  function setResults(html, active) {
    results.innerHTML = html;
    root.classList.toggle("is-active", !!active);
  }

  function render(data, term) {
    var products = data && data.resources && data.resources.results &&
      data.resources.results.products || [];

    if (!products.length) {
      setResults('<div class="wp-search-results__hint">No products found.</div>', true);
      return;
    }

    var html = '<div class="wp-inline-search__grid">';
    products.slice(0, Math.min(8, parseInt(document.querySelector("[data-wp-search-limit]")?.getAttribute("data-wp-search-limit") || "6", 10))).forEach(function (p) {
      var img = p.image || (p.featured_image && p.featured_image.url) || "";
      html += '<a class="wp-inline-search__item" href="' + esc(p.url) + '">'
        + '<div class="wp-inline-search__media">'
        + (img ? '<img src="' + esc(img) + '" alt="' + esc(p.title) + '" loading="lazy">' : "")
        + '</div>'
        + '<div class="wp-inline-search__info">'
        + '<span class="wp-inline-search__title">' + esc(p.title) + '</span>'
        + '<span class="wp-inline-search__price">' + esc(p.price || p.price_min || "") + '</span>'
        + '</div></a>';
    });
    html += '</div>'
      + '<a class="wp-inline-search__view-all" href="/search?q=' + encodeURIComponent(term) + '">View all results ↗</a>';

    setResults(html, true);
  }

  function search(term) {
    if (controller) controller.abort();

    if (!term.trim()) {
      setResults("", false);
      return;
    }

    controller = new AbortController();
    setResults('<div class="wp-search-results__hint">Searching...</div>', true);

    fetch("/search/suggest.json?q=" + encodeURIComponent(term) +
      "&resources[type]=product&resources[limit]=" + (document.querySelector("[data-wp-search-limit]")?.getAttribute("data-wp-search-limit") || "6") + "&resources[options][unavailable_products]=hide", {
        signal: controller.signal,
        headers: { "Accept": "application/json" }
      })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) { if (data) render(data, term); })
      .catch(function (err) {
        if (err && err.name === "AbortError") return;
        setResults('<div class="wp-search-results__hint">Search is temporarily unavailable.</div>', true);
      });
  }

  input.addEventListener("input", function () {
    root.classList.toggle("has-value", !!input.value.trim());
    clearTimeout(timer);
    timer = setTimeout(function () { search(input.value); }, 160);
  });

  clear.addEventListener("click", function () {
    input.value = "";
    root.classList.remove("has-value");
    setResults("", false);
    input.focus();
  });

  document.addEventListener("click", function (event) {
    if (!root.contains(event.target)) root.classList.remove("is-active");
  });
})();


/* WALPEX Quick View / Quick Add v2.0 */
(function () {
  "use strict";

  var modal = document.querySelector("[data-wp-quick-view-modal]");
  if (!modal) return;

  var cartUrl = modal.getAttribute("data-wp-cart-url") || "/cart";
  var content = modal.querySelector("[data-wp-quick-content]");
  var controller = null;

  function esc(v) {
    return String(v == null ? "" : v).replace(/[&<>"']/g, function (m) {
      return ({ "&":"&amp;", "<":"&lt;", ">":"&gt;", '"':"&quot;", "'":"&#39;" })[m];
    });
  }

  function money(cents, currency) {
    if (cents == null) return "";
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currency || "USD"
      }).format(Number(cents) / 100);
    } catch (e) {
      return (Number(cents) / 100).toFixed(2);
    }
  }

  function open(handle) {
    modal.classList.add("is-open");
    modal.setAttribute("aria-hidden", "false");
    document.documentElement.style.overflow = "hidden";
    content.innerHTML = '<div class="wp-quick-view__loading">Loading...</div>';

    if (controller) controller.abort();
    controller = new AbortController();

    fetch("/products/" + encodeURIComponent(handle) + ".js", {
      signal: controller.signal,
      headers: { "Accept": "application/json" }
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (!p) throw new Error("Product unavailable");

        var image = p.featured_image || (p.images && p.images[0]) || "";
        var variants = p.variants || [];
        var options = p.options || [];

        function variantImage(v) {
          if (!v) return image;
          if (v.featured_image && (v.featured_image.src || v.featured_image)) {
            return v.featured_image.src || v.featured_image;
          }
          if (v.featured_media && (v.featured_media.preview_image || v.featured_media.preview_image_src)) {
            return v.featured_media.preview_image || v.featured_media.preview_image_src;
          }
          if (v.image_id && p.images && p.images.length) {
            var found = p.images.find(function (src, idx) {
              return p.image_ids && p.image_ids[idx] === v.image_id;
            });
            if (found) return found;
          }
          return image;
        }

        var html = '<div class="wp-quick-view__media">'
          + '<img data-wp-quick-image src="' + esc(image) + '" alt="' + esc(p.title) + '">'
          + '</div><div class="wp-quick-view__info">'
          + '<div class="wp-quick-view__vendor">' + esc(p.vendor || "") + '</div>'
          + '<h2 class="wp-quick-view__title">' + esc(p.title) + '</h2>'
          + '<div class="wp-quick-view__price" data-wp-quick-price>' + money(p.price, p.currency) + '</div>'
          + '<form data-wp-quick-form>';

        if (options.length && variants.length > 1) {
          options.forEach(function (opt, i) {
            html += '<div class="wp-quick-view__option">'
              + '<label class="wp-quick-view__option-label">' + esc(opt.name) + '</label>'
              + '<select data-wp-option-index="' + i + '">';
            var seen = {};
            variants.forEach(function (v) {
              var val = v.options && v.options[i];
              if (val != null && !seen[val]) {
                seen[val] = true;
                html += '<option value="' + esc(val) + '">' + esc(val) + '</option>';
              }
            });
            html += '</select></div>';
          });
        }

        html += '<div class="wp-quick-view__qty">'
          + '<label>Qty</label><input type="number" min="1" value="1" data-wp-quick-qty></div>'
          + '<button class="wp-quick-view__add" type="submit">Add to cart</button>'
          + '<div class="wp-quick-view__status" data-wp-quick-status></div>'
          + '<a class="wp-quick-view__full" href="' + esc(p.url) + '">View full product ↗</a>'
          + '</form></div>';

        content.innerHTML = html;

        var form = content.querySelector("[data-wp-quick-form]");
        var quickImage = content.querySelector("[data-wp-quick-image]");
        var quickPrice = content.querySelector("[data-wp-quick-price]");

        function getSelectedVariant() {
          var selected = [];
          content.querySelectorAll("[data-wp-option-index]").forEach(function (s) {
            selected[Number(s.dataset.wpOptionIndex)] = s.value;
          });

          if (variants.length === 1) return variants[0];
          if (!selected.length) return variants[0] || null;

          return variants.find(function (v) {
            return (v.options || []).every(function (value, idx) {
              return value === selected[idx];
            });
          }) || null;
        }

        function updateVariantPreview() {
          var variant = getSelectedVariant();
          if (!variant) return;

          var nextImage = variantImage(variant);
          if (nextImage && quickImage && quickImage.src !== nextImage) {
            quickImage.style.opacity = "0";
            setTimeout(function () {
              quickImage.src = nextImage;
              quickImage.onload = function () {
                quickImage.style.opacity = "1";
              };
            }, 90);
          }

          if (quickPrice) quickPrice.textContent = money(variant.price, p.currency);

          var addButton = content.querySelector(".wp-quick-view__add");
          if (addButton) {
            addButton.disabled = !variant.available;
            addButton.textContent = variant.available ? "Add to cart" : "Sold out";
          }
        }

        content.querySelectorAll("[data-wp-option-index]").forEach(function (select) {
          select.addEventListener("change", updateVariantPreview);
        });

        updateVariantPreview();

        form.addEventListener("submit", function (e) {
          e.preventDefault();

          var variant = getSelectedVariant();
          var status = content.querySelector("[data-wp-quick-status]");
          var button = content.querySelector(".wp-quick-view__add");

          if (!variant || !variant.available) {
            status.textContent = "This variant is unavailable.";
            return;
          }

          button.disabled = true;
          status.textContent = "Adding...";

          var qty = Math.max(1, parseInt(content.querySelector("[data-wp-quick-qty]").value, 10) || 1);

          fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", "Accept": "application/json" },
            body: JSON.stringify({ items: [{ id: variant.id, quantity: qty }] })
          })
            .then(function (r) { return r.ok ? r.json() : r.json().then(function () { throw new Error(); }); })
            .then(function () {
              status.innerHTML = '<span>Added to cart.</span> <button type="button" class="wp-quick-view__cart-link" data-wp-open-cart>View cart ↗</button>';
              button.disabled = false;
              document.dispatchEvent(new CustomEvent("wp:cart-updated"));
            })
            .catch(function () {
              status.textContent = "Could not add to cart. Please try again.";
              button.disabled = false;
            });
        });
      })
      .catch(function (err) {
        if (err && err.name === "AbortError") return;
        content.innerHTML = '<div class="wp-quick-view__loading">Product unavailable.</div>';
      });
  }

  function close() {
    modal.classList.remove("is-open");
    modal.setAttribute("aria-hidden", "true");
    document.documentElement.style.overflow = "";
  }

  document.addEventListener("click", function (e) {
    var trigger = e.target.closest("[data-wp-quick-view]");
    if (trigger) {
      e.preventDefault();
      e.stopPropagation();
      open(trigger.dataset.productHandle);
      return;
    }
    if (e.target.closest("[data-wp-quick-close]")) {
      e.preventDefault();
      close();
    }
  }, true);

  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && modal.classList.contains("is-open")) close();
  });
})();


/* WALPEX Product Recommendations v2.5 */
(function () {
  "use strict";

  var root = document.querySelector("[data-wp-recommendations]");
  if (!root) return;

  var rail = root.querySelector("[data-wp-reco-rail]");
  var prev = root.querySelector("[data-wp-reco-prev]");
  var next = root.querySelector("[data-wp-reco-next]");
  var productId = root.getAttribute("data-product-id");
  if (!rail || !productId) return;

  function esc(v) {
    return String(v == null ? "" : v).replace(/[&<>"']/g, function (m) {
      return ({ "&":"&amp;", "<":"&lt;", ">":"&gt;", '"':"&quot;", "'":"&#39;" })[m];
    });
  }

  function updateNav() {
    var max = rail.scrollWidth - rail.clientWidth;
    if (prev) prev.disabled = rail.scrollLeft <= 2;
    if (next) next.disabled = rail.scrollLeft >= max - 2;
  }

  fetch("/recommendations/products.json?product_id=" + encodeURIComponent(productId) + "&limit=8")
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (data) {
      var products = data && data.products || [];
      if (!products.length) {
        root.remove();
        return;
      }

      rail.innerHTML = products.map(function (p) {
        var img = p.featured_image || (p.images && p.images[0]) || "";
        return '<a class="wp-reco-card" href="' + esc(p.url) + '">'
          + '<div class="wp-reco-card__media">'
          + (img ? '<img src="' + esc(img) + '" alt="' + esc(p.title) + '" loading="lazy">' : "")
          + '</div>'
          + '<div class="wp-reco-card__info">'
          + '<span class="wp-reco-card__title">' + esc(p.title) + '</span>'
          + '<span class="wp-reco-card__price">' + esc(p.price || "") + '</span>'
          + '</div>'
          + (p.vendor ? '<div class="wp-reco-card__vendor">' + esc(p.vendor) + '</div>' : "")
          + '</a>';
      }).join("");

      root.classList.add("is-ready");
      updateNav();
    })
    .catch(function () {
      root.remove();
    });

  function step() {
    var card = rail.querySelector(".wp-reco-card");
    return card ? card.getBoundingClientRect().width + 24 : rail.clientWidth;
  }

  if (prev) prev.addEventListener("click", function () {
    rail.scrollBy({ left: -step(), behavior: "smooth" });
  });
  if (next) next.addEventListener("click", function () {
    rail.scrollBy({ left: step(), behavior: "smooth" });
  });

  rail.addEventListener("scroll", updateNav, { passive: true });
  window.addEventListener("resize", updateNav);
})();


/* WALPEX Complementary Products v2.6 */
(function () {
  "use strict";

  var root = document.querySelector("[data-wp-complementary]");
  if (!root) return;

  var rail = root.querySelector("[data-wp-pair-rail]");
  var prev = root.querySelector("[data-wp-pair-prev]");
  var next = root.querySelector("[data-wp-pair-next]");
  var productId = root.getAttribute("data-product-id");
  if (!rail || !productId) return;

  function esc(v) {
    return String(v == null ? "" : v).replace(/[&<>"']/g, function (m) {
      return ({ "&":"&amp;", "<":"&lt;", ">":"&gt;", '"':"&quot;", "'":"&#39;" })[m];
    });
  }

  function updateNav() {
    var max = rail.scrollWidth - rail.clientWidth;
    if (prev) prev.disabled = rail.scrollLeft <= 2;
    if (next) next.disabled = rail.scrollLeft >= max - 2;
  }

  var rootPath = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
  var url = rootPath + "recommendations/products.json?product_id="
    + encodeURIComponent(productId) + "&limit=6&intent=complementary";

  fetch(url, { headers: { "Accept": "application/json" } })
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (data) {
      var products = data && data.products || [];
      if (!products.length) {
        root.remove();
        return;
      }

      // Show the strongest recommendations first, with a compact 2–3 item
      // presentation while allowing horizontal navigation for more.
      products = products.slice(0, 6);

      rail.innerHTML = products.map(function (p) {
        var img = p.featured_image || (p.images && p.images[0]) || "";
        return '<a class="wp-pair-card" href="' + esc(p.url) + '">'
          + '<div class="wp-pair-card__media">'
          + (img ? '<img src="' + esc(img) + '" alt="' + esc(p.title) + '" loading="lazy">' : "")
          + '</div>'
          + '<div>'
          + '<div class="wp-pair-card__title">' + esc(p.title) + '</div>'
          + (p.vendor ? '<div class="wp-pair-card__vendor">' + esc(p.vendor) + '</div>' : "")
          + '</div>'
          + '<div class="wp-pair-card__price">' + esc(p.price || "") + '</div>'
          + '</a>';
      }).join("");

      updateNav();
    })
    .catch(function () {
      root.remove();
    });

  function step() {
    var card = rail.querySelector(".wp-pair-card");
    return card ? card.getBoundingClientRect().width + 14 : rail.clientWidth;
  }

  if (prev) prev.addEventListener("click", function () {
    rail.scrollBy({ left: -step(), behavior: "smooth" });
  });
  if (next) next.addEventListener("click", function () {
    rail.scrollBy({ left: step(), behavior: "smooth" });
  });
  rail.addEventListener("scroll", updateNav, { passive: true });
  window.addEventListener("resize", updateNav);
})();


/* WALPEX Recently Viewed v2.7 — browser-local history */
(function () {
  "use strict";

  var root = document.querySelector("[data-wp-recently-viewed]");
  if (!root) return;

  var rail = root.querySelector("[data-wp-recently-rail]");
  var clear = root.querySelector("[data-wp-recently-clear]");
  if (!rail) return;

  var KEY = "wp_recently_viewed_v1";
  var MAX = 8;
  var currentProduct = null;

  try {
    currentProduct = window.Shopify && ShopifyAnalytics && ShopifyAnalytics.meta &&
      ShopifyAnalytics.meta.product ? ShopifyAnalytics.meta.product.id : null;
  } catch (e) {}

  function read() {
    try {
      var data = JSON.parse(localStorage.getItem(KEY) || "[]");
      return Array.isArray(data) ? data : [];
    } catch (e) {
      return [];
    }
  }

  function write(items) {
    try { localStorage.setItem(KEY, JSON.stringify(items.slice(0, MAX))); } catch (e) {}
  }

  function esc(v) {
    return String(v == null ? "" : v).replace(/[&<>"']/g, function (m) {
      return ({ "&":"&amp;", "<":"&lt;", ">":"&gt;", '"':"&quot;", "'":"&#39;" })[m];
    });
  }

  function isProductPage() {
    return !!document.querySelector('form[action*="/cart/add"]');
  }

  function saveCurrentProduct() {
    if (!isProductPage()) return;

    var productData = null;
    try {
      if (window.meta && meta.product) productData = meta.product;
      if (!productData && window.ShopifyAnalytics && ShopifyAnalytics.meta) {
        productData = ShopifyAnalytics.meta.product;
      }
    } catch (e) {}

    if (!productData || !productData.id) return;

    var title = document.querySelector("h1");
    var image = document.querySelector('meta[property="og:image"]');
    var canonical = document.querySelector('link[rel="canonical"]');

    var item = {
      id: String(productData.id),
      title: title ? title.textContent.trim() : "Product",
      url: canonical ? canonical.href : window.location.pathname,
      image: image ? image.content : "",
      price: ""
    };

    var items = read().filter(function (x) { return String(x.id) !== item.id; });
    items.unshift(item);
    write(items);
  }

  function render() {
    var items = read().filter(function (x) {
      return x && x.url && (!currentProduct || String(x.id) !== String(currentProduct));
    }).slice(0, 6);

    if (!items.length) {
      root.classList.remove("is-visible");
      root.setAttribute("aria-hidden", "true");
      return;
    }

    rail.innerHTML = items.map(function (p) {
      return '<a class="wp-recent-card" href="' + esc(p.url) + '">'
        + '<div class="wp-recent-card__media">'
        + (p.image ? '<img src="' + esc(p.image) + '" alt="' + esc(p.title) + '" loading="lazy">' : "")
        + '</div>'
        + '<div class="wp-recent-card__info">'
        + '<span class="wp-recent-card__title">' + esc(p.title) + '</span>'
        + (p.price ? '<span class="wp-recent-card__price">' + esc(p.price) + '</span>' : "")
        + '</div>'
        + '</a>';
    }).join("");

    root.classList.add("is-visible");
    root.setAttribute("aria-hidden", "false");
  }

  if (clear) {
    clear.addEventListener("click", function () {
      write([]);
      root.classList.remove("is-visible");
      root.setAttribute("aria-hidden", "true");
    });
  }

  render();
  saveCurrentProduct();
  render();
})();


/* WALPEX Variant Swatches v2.8 — collection cards */
(function () {
  "use strict";

  function initVariantSwatches(scope) {
    var root = scope || document;
    root.querySelectorAll("[data-wp-card-swatches]").forEach(function (swatches) {
      if (swatches.dataset.wpSwatchesInit) return;
      swatches.dataset.wpSwatchesInit = "true";

      var card = swatches.closest("[data-wp-catalog-card]");
      if (!card) return;

      var media = card.querySelector(".wp-catalog-card__media");
      var primary = card.querySelector(".wp-catalog-card__image--primary");
      var secondary = card.querySelector(".wp-catalog-card__image--secondary");
      var price = card.querySelector(".wp-catalog-card__price");
      var productUrl = media && media.dataset.wpCardUrl;

      swatches.addEventListener("click", function (event) {
        var button = event.target.closest("[data-wp-swatch]");
        if (!button) return;

        event.preventDefault();
        event.stopPropagation();

        swatches.querySelectorAll("[data-wp-swatch]").forEach(function (b) {
          var selected = b === button;
          b.classList.toggle("is-selected", selected);
          b.setAttribute("aria-pressed", selected ? "true" : "false");
        });

        var image = button.dataset.variantImage;
        var alt = button.dataset.variantImageAlt || "";
        if (image && primary) {
          // Keep src and srcset on the same variant image so the browser
          // cannot fall back to the card's original responsive candidates.
          primary.removeAttribute("srcset");
          primary.src = image;
          primary.setAttribute("data-wp-selected-variant-image", image);
          primary.alt = alt;
          primary.setAttribute("loading", "eager");
        }

        // The second card image is a hover effect. Once a color/variant is
        // selected, hide it so it cannot visually cover the selected image.
        if (secondary) {
          secondary.style.opacity = "0";
          secondary.style.visibility = "hidden";
        }

        if (price && button.dataset.variantPrice) {
          var cents = Number(button.dataset.variantPrice);
          var currency = (window.WALPEX_CART && WALPEX_CART.currency) || "GBP";
          try {
            price.textContent = new Intl.NumberFormat(
              document.documentElement.lang || undefined,
              { style: "currency", currency: currency }
            ).format(cents / 100);
          } catch (e) {
            price.textContent = currency + " " + (cents / 100).toFixed(2);
          }
        }

        // Keep the selected variant available to Quick View integrations.
        if (media) media.dataset.selectedVariantId = button.dataset.variantId || "";

        // On touch devices the second image should not obscure the selected variant.
        if (secondary) secondary.classList.remove("is-hovering");
      });

      // A selected variant remains active. Restore only the hover layer
      // when the swatch area is left; never restore the old primary image.
      swatches.addEventListener("mouseleave", function () {
        if (secondary) {
          secondary.style.opacity = "";
          secondary.style.visibility = "";
        }
      });

      // Clicking the media itself still navigates like the old full-card link.
      if (media && productUrl) {
        media.addEventListener("click", function (event) {
          if (event.target.closest("[data-wp-quick-view]") ||
              event.target.closest("[data-wp-swatch]")) return;
          window.location.href = productUrl;
        });
        media.setAttribute("role", "link");
        media.setAttribute("tabindex", "0");
        media.addEventListener("keydown", function (event) {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            window.location.href = productUrl;
          }
        });
      }
    });
  }

  initVariantSwatches(document);
  document.addEventListener("shopify:section:load", function (event) {
    initVariantSwatches(event.target);
  });
})();


/* WALPEX Wishlist v2.9 — guest wishlist stored locally */
(function () {
  "use strict";

  var KEY = "wp_wishlist_v1";
  var MAX = 50;

  function read() {
    try {
      var data = JSON.parse(localStorage.getItem(KEY) || "[]");
      return Array.isArray(data) ? data : [];
    } catch (e) { return []; }
  }

  function write(items) {
    try { localStorage.setItem(KEY, JSON.stringify(items.slice(0, MAX))); } catch (e) {}
    document.dispatchEvent(new CustomEvent("wp:wishlist-updated", { detail: { items: items } }));
  }

  function getId(el) {
    return String(el.getAttribute("data-wp-wishlist-id") || "");
  }

  function isSaved(id) {
    return read().some(function (x) { return String(x.id) === String(id); });
  }

  function toggle(item) {
    if (!item || !item.id) return;
    var items = read();
    var exists = items.some(function (x) { return String(x.id) === String(item.id); });

    if (exists) {
      items = items.filter(function (x) { return String(x.id) !== String(item.id); });
    } else {
      items.unshift(item);
    }
    write(items);
    updateButtons();
    renderWishlist();
  }

  function updateButtons() {
    document.querySelectorAll("[data-wp-wishlist-id]").forEach(function (button) {
      var saved = isSaved(getId(button));
      button.classList.toggle("is-saved", saved);
      button.setAttribute("aria-pressed", saved ? "true" : "false");
      var label = button.querySelector("[data-wp-wishlist-label]");
      if (label) label.textContent = saved ? "Saved" : "Save";
      button.setAttribute("aria-label", saved ? "Remove from wishlist" : "Add to wishlist");
    });

    var count = read().length;
    document.querySelectorAll("[data-wp-wishlist-count-global]").forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  function renderWishlist() {
    var root = document.querySelector("[data-wp-wishlist]");
    if (!root) return;

    var grid = root.querySelector("[data-wp-wishlist-grid]");
    var empty = root.querySelector("[data-wp-wishlist-empty]");
    var count = root.querySelector("[data-wp-wishlist-count]");
    var items = read();

    root.classList.toggle("is-visible", items.length > 0);
    root.classList.toggle("is-empty", items.length === 0);
    root.setAttribute("aria-hidden", items.length ? "false" : "true");
    if (count) count.textContent = items.length;

    if (!grid) return;
    grid.innerHTML = items.map(function (p) {
      var image = p.image || "";
      return '<article class="wp-wish-card" data-wp-wish-card data-id="' + esc(p.id) + '">'
        + '<div class="wp-wish-card__media">'
        + (image ? '<a href="' + esc(p.url) + '"><img src="' + esc(image) + '" alt="' + esc(p.title) + '" loading="lazy"></a>' : "")
        + '<button type="button" class="wp-wish-card__remove" data-wp-wish-remove aria-label="Remove from wishlist">×</button>'
        + '</div>'
        + '<div class="wp-wish-card__info">'
        + '<a class="wp-wish-card__title" href="' + esc(p.url) + '">' + esc(p.title) + '</a>'
        + (p.price ? '<span class="wp-wish-card__price">' + esc(p.price) + '</span>' : "")
        + '</div>'
        + '<div class="wp-wish-card__actions">'
        + '<a class="wp-wish-card__view" href="' + esc(p.url) + '">View</a>'
        + '<button type="button" class="wp-wish-card__add" data-wp-wish-add data-variant-id="' + esc(p.variantId || "") + '">Add to cart</button>'
        + '</div>'
        + '</article>';
    }).join("");
  }

  function esc(v) {
    return String(v == null ? "" : v).replace(/[&<>"']/g, function (m) {
      return ({ "&":"&amp;", "<":"&lt;", ">":"&gt;", '"':"&quot;", "'":"&#39;" })[m];
    });
  }

  document.addEventListener("click", function (event) {
    var button = event.target.closest("[data-wp-wishlist-id]");
    if (button) {
      event.preventDefault();
      toggle({
        id: getId(button),
        title: button.getAttribute("data-wp-wishlist-title") || "Product",
        url: button.getAttribute("data-wp-wishlist-url") || "#",
        image: button.getAttribute("data-wp-wishlist-image") || "",
        price: button.getAttribute("data-wp-wishlist-price") || "",
        variantId: button.getAttribute("data-wp-wishlist-variant") || ""
      });
      return;
    }

    var remove = event.target.closest("[data-wp-wish-remove]");
    if (remove) {
      event.preventDefault();
      var card = remove.closest("[data-wp-wish-card]");
      var id = card && card.getAttribute("data-id");
      write(read().filter(function (x) { return String(x.id) !== String(id); }));
      renderWishlist();
      updateButtons();
      return;
    }

    var add = event.target.closest("[data-wp-wish-add]");
    if (add) {
      event.preventDefault();
      var card = add.closest("[data-wp-wish-card]");
      var id = card && card.getAttribute("data-id");
      var item = read().find(function (x) { return String(x.id) === String(id); });
      if (!item || !item.variantId) {
        window.location.href = item ? item.url : "#";
        return;
      }

      add.disabled = true;
      fetch((window.Shopify && Shopify.routes && Shopify.routes.root || "/") + "cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ items: [{ id: Number(item.variantId), quantity: 1 }] })
      }).then(function () {
        document.dispatchEvent(new CustomEvent("wp:cart-updated"));
        add.textContent = "Added";
        setTimeout(function () {
          add.textContent = "Add to cart";
          add.disabled = false;
        }, 900);
      }).catch(function () {
        add.disabled = false;
      });
    }
  });

  document.addEventListener("wp:wishlist-updated", updateButtons);
  document.addEventListener("DOMContentLoaded", function () {
    updateButtons();
    renderWishlist();
  });
})();


/* WALPEX Wishlist UI buttons v2.9.1 */
(function () {
  "use strict";

  var HEART = "♡";
  var FILLED = "♥";

  function esc(v) {
    return String(v == null ? "" : v).replace(/[&<>"']/g, function (m) {
      return ({ "&":"&amp;", "<":"&lt;", ">":"&gt;", '"':"&quot;", "'":"&#39;" })[m];
    });
  }

  function productFromCard(card) {
    if (!card) return null;

    var link = card.querySelector('a[href*="/products/"]');
    var image = card.querySelector("img");
    var title = card.querySelector("[data-product-title], .card__heading, .product-card__title, .wp-reco-card__title, .wp-recent-card__title");
    var price = card.querySelector("[data-product-price], .price, .product-card__price, .wp-reco-card__price, .wp-recent-card__price");

    if (!link) return null;

    var url = link.href;
    var id = card.getAttribute("data-product-id") || card.getAttribute("data-pid") ||
      (new URL(url, window.location.origin).pathname);

    var variant = card.querySelector("[data-variant-id], input[name='id']");
    return {
      id: id,
      title: title ? title.textContent.trim() : (link.getAttribute("aria-label") || "Product"),
      url: url,
      image: image ? (image.currentSrc || image.src) : "",
      price: price ? price.textContent.trim() : "",
      variantId: variant ? (variant.value || variant.getAttribute("data-variant-id")) : ""
    };
  }

  function addButton(container, item, extraClass) {
    if (!container || !item || container.querySelector("[data-wp-wishlist-id]")) return;

    var button = document.createElement("button");
    button.type = "button";
    button.className = "wp-wishlist-toggle " + (extraClass || "");
    button.setAttribute("data-wp-wishlist-id", item.id);
    button.setAttribute("data-wp-wishlist-title", item.title);
    button.setAttribute("data-wp-wishlist-url", item.url);
    button.setAttribute("data-wp-wishlist-image", item.image);
    button.setAttribute("data-wp-wishlist-price", item.price);
    if (item.variantId) button.setAttribute("data-wp-wishlist-variant", item.variantId);
    button.setAttribute("aria-pressed", "false");
    button.setAttribute("aria-label", "Add to wishlist");
    button.innerHTML = '<span aria-hidden="true">' + HEART + '</span>';
    container.appendChild(button);
  }

  function scan() {
    document.querySelectorAll(".card-wrapper, .product-card, [data-product-card], .wp-reco-card, .wp-recent-card").forEach(function (card) {
      if (card.classList.contains("wp-catalog-card")) return;
      var item = productFromCard(card);
      if (!item) return;

      var media = card.querySelector(".card__media, .product-card__media, .wp-reco-card__media, .wp-recent-card__media") || card;
      addButton(media, item, "wp-wishlist-toggle--card");
    });

    var productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm && !document.querySelector("[data-wp-product-wishlist]")) {
      var productTitle = document.querySelector("h1");
      var productImage = document.querySelector('meta[property="og:image"]');
      var variant = productForm.querySelector('input[name="id"], select[name="id"]');

      var wrap = document.createElement("div");
      wrap.setAttribute("data-wp-product-wishlist", "true");

      var item = {
        id: (productForm.getAttribute("data-product-id") || (variant && variant.value) || location.pathname),
        title: productTitle ? productTitle.textContent.trim() : document.title,
        url: location.href,
        image: productImage ? productImage.content : "",
        price: "",
        variantId: variant ? (variant.value || "") : ""
      };

      addButton(wrap, item, "wp-wishlist-toggle--product");
      productForm.parentNode.insertBefore(wrap, productForm);
    }

    if (typeof updateButtons === "function") updateButtons();
  }

  document.addEventListener("DOMContentLoaded", scan);
  window.addEventListener("load", scan);
  document.addEventListener("shopify:section:load", scan);
  setTimeout(scan, 500);
})();


/* WALPEX Wishlist Header v2.9.2 */
(function () {
  "use strict";

  function placeHeaderWishlist() {
    var item = document.querySelector("[data-wp-header-wishlist]");
    if (!item || item.dataset.wpPlaced === "true") return;

    var candidates = [
      ".header__icons",
      ".header__actions",
      ".header__right",
      ".header-actions",
      "header .header__icon-list",
      "header"
    ];

    var target = null;
    for (var i = 0; i < candidates.length; i++) {
      target = document.querySelector(candidates[i]);
      if (target) break;
    }
    if (!target) return;

    var cart = target.querySelector("[data-wp-header-cart]");
    if (cart) {
      target.insertBefore(item, cart);
    } else {
      target.appendChild(item);
    }
    item.dataset.wpPlaced = "true";

    item.addEventListener("click", function (event) {
      var wishlist = document.querySelector("[data-wp-wishlist]");
      if (!wishlist) return;
      event.preventDefault();
      wishlist.classList.add("is-visible");
      wishlist.classList.remove("is-empty");
      wishlist.setAttribute("aria-hidden", "false");
      wishlist.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  function updateHeaderWishlist() {
    var count = 0;
    try {
      count = JSON.parse(localStorage.getItem("wp_wishlist_v1") || "[]").length;
    } catch (e) {}

    document.querySelectorAll("[data-wp-wishlist-count-global]").forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });

    document.querySelectorAll("[data-wp-header-wishlist]").forEach(function (item) {
      item.classList.toggle("is-active", count > 0);
      item.setAttribute("aria-label", count ? "Wishlist, " + count + " saved items" : "Wishlist");
    });
  }

  document.addEventListener("DOMContentLoaded", function () {
    placeHeaderWishlist();
    updateHeaderWishlist();
  });
  window.addEventListener("load", function () {
    placeHeaderWishlist();
    updateHeaderWishlist();
  });
  document.addEventListener("wp:wishlist-updated", updateHeaderWishlist);
  document.addEventListener("shopify:section:load", function () {
    setTimeout(function () {
      placeHeaderWishlist();
      updateHeaderWishlist();
    }, 50);
  });
})();


/* WALPEX Wishlist click isolation v2.9.2 */
(function () {
  "use strict";

  document.addEventListener("click", function (event) {
    var button = event.target.closest(".wp-catalog-wishlist");
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();

    var item = {
      id: button.getAttribute("data-wp-wishlist-id"),
      title: button.getAttribute("data-wp-wishlist-title") || "Product",
      url: button.getAttribute("data-wp-wishlist-url") || "#",
      image: button.getAttribute("data-wp-wishlist-image") || "",
      price: button.getAttribute("data-wp-wishlist-price") || "",
      variantId: button.getAttribute("data-wp-wishlist-variant") || ""
    };

    var key = "wp_wishlist_v1";
    var items = [];
    try { items = JSON.parse(localStorage.getItem(key) || "[]"); } catch (e) {}
    if (!Array.isArray(items)) items = [];

    var exists = items.some(function (x) { return String(x.id) === String(item.id); });
    if (exists) {
      items = items.filter(function (x) { return String(x.id) !== String(item.id); });
      button.classList.remove("is-saved");
      button.setAttribute("aria-pressed", "false");
      button.textContent = "♡";
    } else {
      items.unshift(item);
      button.classList.add("is-saved");
      button.setAttribute("aria-pressed", "true");
      button.textContent = "♥";
    }

    try { localStorage.setItem(key, JSON.stringify(items.slice(0, 50))); } catch (e) {}

    document.dispatchEvent(new CustomEvent("wp:wishlist-updated", {
      detail: { items: items }
    }));
  }, true);
})();


/* WALPEX Mobile Menu editor-safe reinit v2.9.2 */
(function () {
  "use strict";

  function initMobileMenu() {
    var toggle = document.querySelector("[data-wp-menu-toggle]");
    var menu = document.querySelector("[data-wp-mobile-menu]");
    if (!toggle || !menu || toggle.dataset.wpMobileReady === "true") return;

    toggle.dataset.wpMobileReady = "true";

    function closeMenu() {
      menu.hidden = true;
      toggle.setAttribute("aria-expanded", "false");
      document.body.classList.remove("wp-menu-open");
    }

    toggle.addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();
      var open = toggle.getAttribute("aria-expanded") === "true";
      toggle.setAttribute("aria-expanded", open ? "false" : "true");
      menu.hidden = open;
      document.body.classList.toggle("wp-menu-open", !open);
    });

    menu.querySelectorAll("[data-wp-menu-close]").forEach(function (el) {
      el.addEventListener("click", function (e) {
        e.preventDefault();
        closeMenu();
      });
    });

    menu.querySelectorAll("a").forEach(function (a) {
      a.addEventListener("click", closeMenu);
    });
  }

  document.addEventListener("DOMContentLoaded", initMobileMenu);
  window.addEventListener("load", initMobileMenu);
  document.addEventListener("shopify:section:load", function () {
    setTimeout(initMobileMenu, 30);
  });
  window.wpCollectionsCinematicTransition();
})();

// NESTORI LAB 3.2.8.8.50 — hero camera zoom while entering Collections.
(() => {
  const initHeroCamera = () => {
    const hero = document.querySelector('[data-hero-camera]');
    const collections = document.querySelector('.wp-collections-section');
    if (!hero || !collections || hero.dataset.heroCameraReady === 'true') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    hero.dataset.heroCameraReady = 'true';

    let raf = 0;
    const update = () => {
      raf = 0;
      const rect = collections.getBoundingClientRect();
      const range = Math.max(260, Math.min(window.innerHeight * 0.72, 720));
      const progress = Math.max(0, Math.min(1, (window.innerHeight - rect.top) / range));
      // Zoom the outgoing hero as the Collections section approaches, then restore
      // once the next section is fully established.
      const p = Math.max(0, Math.min(1, (rect.top - 0) / range));
      const scale = 1 + (1 - p) * 0.055;
      const opacity = 1 - (1 - p) * 0.08;
      hero.style.setProperty('--hero-camera-scale', scale.toFixed(4));
      hero.style.setProperty('--hero-camera-opacity', opacity.toFixed(4));
    };
    const request = () => { if (!raf) raf = requestAnimationFrame(update); };
    window.addEventListener('scroll', request, {passive:true});
    window.addEventListener('resize', request);
    request();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initHeroCamera);
  else initHeroCamera();
  document.addEventListener('shopify:section:load', initHeroCamera);
})();

/* NESTORI LAB v3.2.8.8.53 — mobile footer accordions */
(function(){
  function syncFooterAccordions(){
    document.querySelectorAll('[data-footer-accordion]').forEach(function(d){
      if (window.matchMedia('(max-width: 749px)').matches) d.removeAttribute('open');
      else d.setAttribute('open','');
    });
  }
  document.addEventListener('DOMContentLoaded', syncFooterAccordions);
  window.addEventListener('resize', syncFooterAccordions, {passive:true});
})();
